/**
 * AUTOSAVE & PERSISTENCE — методы автосохранения сметы.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: AUTOSAVE & PERSISTENCE).
 *
 * Включает три уровня сохранения:
 *
 *  1. autoSaveQuote() — legacy localStorage backup в ключ `quoteCalc_autosave`.
 *     Используется как safety-net если сервер недоступен.
 *     Запускается debounced из markQuoteAsUnsaved() через scheduleUpdate.
 *
 *  2. saveToLocalStorage() — основной server-autosave (название историческое,
 *     метод фактически шлёт на /api/v1/estimates/:id). Debounce 8 сек.
 *     Использует window.QuoteCalc.state.currentQuoteId как ключ.
 *     ⚠️ Phase 1 fix: до фикса использовал this.currentEstimateId
 *     (никогда не устанавливался) → autosave никогда не срабатывал.
 *
 *  3. prepareEstimateData() — формирует payload для server save.
 *     Возвращает FLAT-структуру, совместимую с loadQuoteFromServer.
 *
 * @module app/autosave
 */

/**
 * Прикрепляет методы автосохранения к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachAutosave(ClassRef) {
    Object.assign(ClassRef.prototype, {
        // ────────────────────────────────────────────────────────────────────
        // Legacy localStorage autosave (safety-net)
        // ────────────────────────────────────────────────────────────────────

        autoSaveQuote() {
            try {
                // Сохраняем только если есть хоть одна услуга
                if (this.state.services.length === 0 && this.state.optionalServices.length === 0 &&
                    this.state.hotels.length === 0 && this.state.flights.length === 0 && this.state.otherServices.length === 0) {
                    return;
                }

                const quoteData = {
                    version: this.QUOTE_VERSION,
                    appVersion: this.APP_VERSION,
                    timestamp: new Date().toISOString(),
                    autosave: true, // Флаг автосохранения
                    metadata: {
                        paxCount: this.state.paxCount,
                        tourStart: this.state.tourStart,
                        tourEnd: this.state.tourEnd,
                        clientName: this.state.clientName,
                        clientPhone: this.state.clientPhone,
                        clientEmail: this.state.clientEmail,
                        quoteComments: this.state.quoteComments,
                        programDescription: this.state.programDescription,
                        accommodations: this.state.accommodations,
                        bookingTerms: this.state.bookingTerms,
                        hiddenMarkup: this.state.hiddenMarkup,
                        partnerCommission: this.state.partnerCommission,
                        partnerCommissionId: this.state.partnerCommissionId || null,
                        prepaymentManual: this.state.prepaymentManual || 0
                    },
                    services: this.state.services,
                    optionalServices: this.state.optionalServices || [],
                    hotels: this.state.hotels || [],
                    flights: this.state.flights || [],
                    otherServices: this.state.otherServices || [],
                    calculations: {
                        baseCost: this.calculateBaseCost(),
                        totalProfit: this.calculateTotalProfit(),
                        clientTotal: this.calculateClientTotal()
                    }
                };

                const jsonString = JSON.stringify(quoteData);
                localStorage.setItem('quoteCalc_autosave', jsonString);
                localStorage.setItem('quoteCalc_autosave_timestamp', Date.now().toString());

                console.log('Автосохранение выполнено');
            } catch (error) {
                console.warn('Ошибка автосохранения:', error);
            }
        },

        // ────────────────────────────────────────────────────────────────────
        // Server-side autosave (через apiClient.saveEstimate)
        // ────────────────────────────────────────────────────────────────────

        /**
         * Подготовка данных сметы для сохранения на сервер.
         * 🔧 FIX (Phase 1 P0): структура должна быть FLAT — совместимо с
         *   loadQuoteFromServer (init.js) и saveQuoteToServer (init.js).
         *   Раньше использовался nested metadata → загрузка показывала пустого клиента.
         * @returns {Object} Данные сметы в формате API (плоская структура).
         */
        prepareEstimateData() {
            return {
                id: this.state.currentQuoteId,
                version: this.QUOTE_VERSION,
                appVersion: this.APP_VERSION,
                timestamp: new Date().toISOString(),
                // Плоская структура — поля верхнего уровня
                paxCount: this.state.paxCount,
                tourStart: this.state.tourStart,
                tourEnd: this.state.tourEnd,
                clientName: this.state.clientName,
                clientPhone: this.state.clientPhone,
                clientEmail: this.state.clientEmail,
                quoteComments: this.state.quoteComments,
                programDescription: this.state.programDescription,
                accommodations: this.state.accommodations,
                bookingTerms: this.state.bookingTerms,
                hiddenMarkup: this.state.hiddenMarkup,
                partnerCommission: this.state.partnerCommission,
                        partnerCommissionId: this.state.partnerCommissionId || null,
                taxRate: this.state.taxRate,
                // Sprint 19a: prepayment manual amount
                prepaymentManual: this.state.prepaymentManual || 0,
                // Phase 8c: multi-currency — валюта клиента для итогов
                clientCurrency: this.state.clientCurrency || 'USD',
                services: this.state.services,
                hotels: this.state.hotels || [],
                flights: this.state.flights || [],
                otherServices: this.state.otherServices || [],
                optionalServices: this.state.optionalServices || [],
                calculations: {
                    baseCost: this.calculateBaseCost(),
                    totalProfit: this.calculateTotalProfit(),
                    clientTotal: this.calculateClientTotal()
                }
            };
        },

        /**
         * Server-autosave (8-сек debounce).
         * Имя метода историческое: до v3 сохранял в localStorage, теперь шлёт на /api/v1/estimates/:id.
         * Для каждого изменения сметы вызывается из markQuoteAsUnsaved() через debouncedAutosave.
         */
        saveToLocalStorage() {
            // ✅ GUARD FLAG: Блокируем autosave во время загрузки сметы
            if (this.isLoadingQuote) {
                console.log('[Autosave] Skipped - loading in progress');
                return;
            }

            // ✅ DEBOUNCE: Очищаем предыдущий таймер автосохранения
            if (this.timeoutIds.autosave) {
                clearTimeout(this.timeoutIds.autosave);
            }

            // ✅ DEBOUNCE: Устанавливаем новый таймер (8 секунд по умолчанию)
            this.timeoutIds.autosave = setTimeout(async () => {
                try {
                    // 🔧 FIX (P0): использовать state.currentQuoteId — единый идентификатор сметы.
                    //   До фикса использовался this.currentEstimateId, который никогда не устанавливался,
                    //   поэтому autosave никогда не срабатывал и пользователи теряли данные между ручными save.
                    let quoteId = this.state.currentQuoteId;

                    // Если у сметы ещё нет ID — генерируем (это первая правка после нового файла)
                    if (!quoteId) {
                        quoteId = this.generateQuoteId();
                        this.state.currentQuoteId = quoteId;
                        console.log('[Autosave] Auto-generated quote ID:', quoteId);
                    }

                    // 🔧 FIX (P0): проверяем все 4 массива услуг + наличие данных клиента,
                    //   а не только state.services (раньше отели/перелёты/прочие не триггерили autosave).
                    const hasAnyServices =
                        (this.state.services?.length || 0) +
                        (this.state.hotels?.length || 0) +
                        (this.state.flights?.length || 0) +
                        (this.state.otherServices?.length || 0) +
                        (this.state.optionalServices?.length || 0) > 0;
                    const hasClientData = !!(this.state.clientName || this.state.clientEmail || this.state.clientPhone);

                    if (!hasAnyServices && !hasClientData) {
                        // Пустая смета — не создаём мусорные записи в БД
                        return;
                    }

                    console.log(`[Autosave] Saving estimate ${quoteId} after ${this.config.debounceDelays.autosave}ms delay`);

                    const estimateData = this.prepareEstimateData();
                    estimateData.id = quoteId;
                    await this.apiClient.saveEstimate(quoteId, estimateData);

                    // Запоминаем ID последней открытой сметы (для авто-открытия после reload)
                    try { localStorage.setItem('lastOpenedEstimateId', quoteId); } catch (_) {}

                    console.log(`✓ Estimate ${quoteId} auto-saved successfully`);

                    // 🆕 Backups v2: каждые N autosaves создаём snapshot.
                    //   Ротация: оставляем 10 последних бэкапов на смету.
                    //   Тихий fail если backup-API недоступен — не блокируем основное сохранение.
                    this._autosaveCounter = (this._autosaveCounter || 0) + 1;
                    const BACKUP_EVERY_N = 3; // backup каждые 3 autosave (≈24 сек активной правки)
                    if (this._autosaveCounter % BACKUP_EVERY_N === 0) {
                        try {
                            await this.apiClient.createBackup(quoteId, estimateData, {
                                reason: 'autosave',
                                filename: this.state.currentQuoteFile || null,
                                pruneKeep: 10
                            });
                            console.log(`✓ Backup snapshot created for ${quoteId}`);
                        } catch (backupErr) {
                            // Не блокируем основное сохранение
                            console.warn('[Backup] Failed to create snapshot (non-fatal):', backupErr.message);
                        }
                    }
                } catch (error) {
                    console.warn('Could not save estimate to server:', error);
                } finally {
                    // Очищаем timeout ID после выполнения
                    this.timeoutIds.autosave = null;
                }
            }, this.config.debounceDelays.autosave);
        }
    });
}
